import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdNumbers,
  MdOutlineAddRoad,
  MdOutlineAlternateEmail,
  MdOutlineCall,
  MdOutlineFlaky,
  MdOutlinePinDrop,
  MdOutlineRestartAlt,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  REPETITION_INDEX,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Contact = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
  streetOptions,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { cities, loading: citiesLoading } = useZipCodeCities(
    get(
      initialValues,
      `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
    ),
  );

  const addressNumberPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS_NUMBER}.value`;
  const addressNumberField = bindField(addressNumberPath, {
    label: SUBSCRIPTION.FORM.NUMBER.LABEL,
    required: isFieldRequired(addressNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const repetitionIndexPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.REPETITION_INDEX}.value`;
  const repetitionIndexField = bindField(repetitionIndexPath, {
    label: SUBSCRIPTION.FORM.REPETITION_INDEX.LABEL,
    required: isFieldRequired(repetitionIndexPath),
    onBlur: () => setPercent(initialValues),
  });

  const streetTypePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET_TYPE}.value`;
  const streetTypeField = bindField(streetTypePath, {
    label: SUBSCRIPTION.FORM.TYPE.LABEL,
    required: isFieldRequired(streetTypePath),
    onBlur: () => setPercent(initialValues),
  });

  const streetPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET}.value`;
  const streetField = bindField(streetPath, {
    label: SUBSCRIPTION.FORM.STREET.LABEL,
    required: isFieldRequired(streetPath),
    onBlur: () => setPercent(initialValues),
  });

  const complementPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`;
  const complementField = bindField(complementPath, {
    label: SUBSCRIPTION.FORM.COMPLEMENT.LABEL,
    required: isFieldRequired(complementPath),
    onBlur: () => setPercent(initialValues),
  });

  const zipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    disabled: true,
  });

  const cityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    onBlur: () => setPercent(initialValues),
  });

  const phonePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`;
  const phoneField = bindField(phonePath, {
    label: SUBSCRIPTION.FORM.MOBILE.LABEL,
    required: isFieldRequired(phonePath),
    onBlur: () => setPercent(initialValues),
  });

  const landlinePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.LANDLINE}.value`;
  const landlineField = bindField(landlinePath, {
    label: SUBSCRIPTION.FORM.LANDLINE.LABEL,
    required: isFieldRequired(landlinePath),
    onBlur: () => setPercent(initialValues),
  });

  const emailPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`;
  const emailField = bindField(emailPath, {
    label: SUBSCRIPTION.FORM.EMAIL.LABEL,
    required: isFieldRequired(emailPath),
    onBlur: () => setPercent(initialValues),
  });

  const acceptContactPath = "project.contact.acceptContact";
  const acceptContactField = bindField(acceptContactPath, {
    label: SUBSCRIPTION.FORM.CONFIRM_CONTACT.LABEL,
    required: isFieldRequired(acceptContactPath),
    onChange: () => setPercent(initialValues),
  });

  const acceptDigitalPath = "project.contact.acceptDigital";
  const acceptDigitalField = bindField(acceptDigitalPath, {
    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.LABEL,
    required: isFieldRequired(acceptDigitalPath),
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.TITLE}
      >
        <div className="grid--1">
          <Form
            type="highlight"
            title={
              SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.ADDRESS.TITLE
            }
          >
            <div className="grid--1">
              <FormField
                path={addressNumberField.path}
                label={addressNumberField.label}
                required={addressNumberField.required}
                disabled={addressNumberField.disabled}
              >
                {(field) => (
                  <Input
                    icon={<MdNumbers size={16} />}
                    placeholder={SUBSCRIPTION.FORM.NUMBER.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={addressNumberField.defaultValue}
                    {...field}
                    onChange={addressNumberField.onChange}
                    onBlur={addressNumberField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={repetitionIndexField.path}
                label={repetitionIndexField.label}
                required={repetitionIndexField.required}
                disabled={repetitionIndexField.disabled}
              >
                {(field) => (
                  <Select
                    showSearch
                    options={REPETITION_INDEX.map((index) => ({
                      value: index,
                      label: index,
                    }))}
                    icon={<MdOutlineRestartAlt size={16} />}
                    placeholder={SUBSCRIPTION.FORM.REPETITION_INDEX.PLACEHOLDER}
                    defaultValue={repetitionIndexField.defaultValue}
                    {...field}
                    onChange={repetitionIndexField.onChange}
                    onBlur={repetitionIndexField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={streetTypeField.path}
                label={streetTypeField.label}
                required={streetTypeField.required}
                disabled={streetTypeField.disabled}
              >
                {(field) => (
                  <Select
                    placeholder={SUBSCRIPTION.FORM.TYPE.PLACEHOLDER}
                    showSearch
                    icon={<MdOutlineAddRoad size={16} />}
                    options={streetOptions}
                    defaultValue={streetTypeField.defaultValue}
                    {...field}
                    onChange={streetTypeField.onChange}
                    onBlur={streetTypeField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={streetField.path}
                label={streetField.label}
                required={streetField.required}
                disabled={streetField.disabled}
              >
                {(field) => (
                  <Input
                    icon={<MdOutlineAddRoad size={16} />}
                    placeholder={SUBSCRIPTION.FORM.STREET.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={streetField.defaultValue}
                    {...field}
                    onChange={streetField.onChange}
                    onBlur={streetField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={complementField.path}
                label={complementField.label}
                required={complementField.required}
                disabled={complementField.disabled}
              >
                {(field) => (
                  <Input
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    icon={<MdOutlineAddRoad size={16} />}
                    placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={complementField.defaultValue}
                    {...field}
                    onChange={complementField.onChange}
                    onBlur={complementField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={zipCodeField.path}
                label={zipCodeField.label}
                required={zipCodeField.required}
                disabled={zipCodeField.disabled}
              >
                {(field) => (
                  <Input
                    icon={<MdOutlinePinDrop size={16} />}
                    placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                    defaultValue={zipCodeField.defaultValue}
                    {...field}
                    onChange={zipCodeField.onChange}
                    onBlur={zipCodeField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={cityField.path}
                label={cityField.label}
                required={cityField.required}
                disabled={cityField.disabled}
              >
                {(field) => (
                  <Select
                    icon={<MdOutlineSouthAmerica size={16} />}
                    placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                    showSearch
                    loading={citiesLoading}
                    options={cities}
                    defaultValue={cityField.defaultValue}
                    {...field}
                    onChange={cityField.onChange}
                    onBlur={cityField.onBlur}
                  />
                )}
              </FormField>
            </div>
          </Form>
          <Form
            type="highlight"
            title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.TITLE}
            subtitle={
              SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.SUBTITLE
            }
          >
            <dvi className="grid--1">
              <Alert
                showIcon
                type="info"
                message={
                  SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.INFO
                }
              />

              <FormField
                path={phoneField.path}
                label={phoneField.label}
                required={phoneField.required}
                disabled={phoneField.disabled}
              >
                {(field) => (
                  <Input
                    icon={<MdOutlineCall size={16} />}
                    placeholder={SUBSCRIPTION.FORM.MOBILE.PLACEHOLDER}
                    type="custom"
                    stripSpaces={true}
                    defaultValue={phoneField.defaultValue}
                    {...field}
                    onChange={phoneField.onChange}
                    onBlur={phoneField.onBlur}
                  />
                )}
              </FormField>

              <FormField
                path={landlineField.path}
                label={landlineField.label}
                required={landlineField.required}
                disabled={landlineField.disabled}
              >
                {(field) => (
                  <Input
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    icon={<MdOutlineCall size={16} />}
                    placeholder={SUBSCRIPTION.FORM.LANDLINE.PLACEHOLDER}
                    type="custom"
                    trim={true}
                    defaultValue={landlineField.defaultValue}
                    {...field}
                    onChange={landlineField.onChange}
                    onBlur={landlineField.onBlur}
                  />
                )}
              </FormField>
            </dvi>
          </Form>

          <FormField
            path={emailField.path}
            label={emailField.label}
            required={emailField.required}
            disabled={emailField.disabled}
          >
            {(field) => (
              <Input
                icon={<MdOutlineAlternateEmail size={16} />}
                placeholder={SUBSCRIPTION.FORM.EMAIL.PLACEHOLDER}
                defaultValue={emailField.defaultValue}
                {...field}
                onChange={emailField.onChange}
                onBlur={emailField.onBlur}
              />
            )}
          </FormField>

          <FormField
            path={acceptContactField.path}
            label={acceptContactField.label}
            required={acceptContactField.required}
            disabled={acceptContactField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlineFlaky size={16} />}
                values={[
                  {
                    value: true,
                    label: SUBSCRIPTION.FORM.CONFIRM_CONTACT.OPTIONS.YES,
                  },
                  {
                    value: false,
                    label: SUBSCRIPTION.FORM.CONFIRM_CONTACT.OPTIONS.NO,
                  },
                ]}
                defaultValue={acceptContactField.defaultValue}
                {...field}
                onChange={acceptContactField.onChange}
                onBlur={acceptContactField.onBlur}
              />
            )}
          </FormField>

          <Alert
            showIcon
            type="info"
            message={SUBSCRIPTION.FORM.CONFIRM_CONTACT.INFO_MCCI}
          />

          <FormField
            path={acceptDigitalField.path}
            label={acceptDigitalField.label}
            required={acceptDigitalField.required}
            disabled={acceptDigitalField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlineFlaky size={16} />}
                values={[
                  {
                    value: true,
                    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.OPTIONS.YES,
                  },
                  {
                    value: false,
                    label: SUBSCRIPTION.FORM.DIGITAL_OPTION.OPTIONS.NO,
                  },
                ]}
                defaultValue={acceptDigitalField.defaultValue}
                {...field}
                onChange={acceptDigitalField.onChange}
                onBlur={acceptDigitalField.onBlur}
              />
            )}
          </FormField>

          <Alert
            showIcon
            type="info"
            message={SUBSCRIPTION.FORM.DIGITAL_OPTION.INFO}
          />
        </div>
      </Form>
    </div>
  );
};

export default Contact;
